import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useGlobalStore } from "../context/UserContext/userContext";
import { syncAuthTokenInUrl } from "../utils/buildPatientAuthUrl";
import {
  creApiRequest,
  isAuthorityMismatchError,
} from "./creAuthConfig";
import { getCreActiveAccount } from "./creAccountUtils";
import { getCreMsalInstance, initializeCreMsal } from "./creMsalInstance";
import { getSafeTokenMeta, logMedmicAuthDiag } from "./creTokenDiagnostics";

/**
 * Enterprise CRE access-token refresh.
 * Phase 0: must NOT publish to MedMic — application sessions stay separate.
 */
export async function refreshCreAccessToken() {
  await initializeCreMsal();
  const instance = getCreMsalInstance();
  const account = getCreActiveAccount(instance);
  if (!account) throw new Error("No CRE account in MSAL cache");

  let response;
  try {
    response = await instance.acquireTokenSilent({
      ...creApiRequest,
      account,
      forceRefresh: true,
    });
  } catch (err) {
    if (
      err instanceof InteractionRequiredAuthError ||
      isAuthorityMismatchError(err)
    ) {
      throw new Error("CRE session expired");
    }
    throw err;
  }

  const token = response?.accessToken;
  if (!token) throw new Error("No token from Entra refresh");

  const meta = getSafeTokenMeta(token);
  logMedmicAuthDiag("enterprise", "token_refresh_candidate", {
    fingerprint: meta.fingerprint,
    aud: meta.aud,
    exp: meta.exp,
    iat: meta.iat,
  });

  const ok = await useGlobalStore.getState().initializeWithCreToken(token);
  if (!ok) {
    logMedmicAuthDiag("enterprise", "token_refresh_validate_failed", {
      fingerprint: meta.fingerprint,
    });
    throw new Error("CRE token validation failed after refresh");
  }

  logMedmicAuthDiag("enterprise", "token_refresh_validated", {
    fingerprint: meta.fingerprint,
    exp: meta.exp,
  });

  syncAuthTokenInUrl(token);

  // Intentionally no MedMic SESSION publish — Enterprise session only.

  return token;
}
